<script setup lang="ts">
import { computed } from 'vue'
import CanvasPreviewSurface, {
  type PreviewDrawItem,
  type PreviewHotspotItem,
} from '@/components/workspace/CanvasPreviewSurface.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type { SettingsDialogTabProps } from '@/features/workspace/settings/types'

const props = defineProps<SettingsDialogTabProps>()

function updateTextChoiceBackImage(value: number) {
  props.talkSetting.SelectButton = value
  props.talkSetting.SelectPic = value
}

const previewDrawItems = computed<PreviewDrawItem[]>(() => [
  {
    key: 'talk-box',
    kind: 'image',
    style: props.previewTalkBoxStyle,
    imageUrl: props.previewTalkBoxUrl,
    placeholderText: '对话框',
  },
  {
    key: 'talk-text',
    kind: 'rect',
    style: props.previewTalkTextRegionStyle,
    text: props.previewSampleContent,
    fillColor: '#1d344a',
    strokeColor: '#6fb6ff',
    textColor: String(props.previewTalkTextRegionStyle.color || '#ffffff'),
    fontSize: Number.parseFloat(String(props.previewTalkTextRegionStyle.fontSize || 24)) || 24,
    fontFamily: String(props.previewTalkTextRegionStyle.fontFamily || 'sans-serif'),
    padding: 8,
  },
  {
    key: 'name-box',
    kind: 'image',
    style: props.previewNameBoxStyle,
    imageUrl: props.previewNameBoxUrl,
    placeholderText: '姓名框',
  },
  {
    key: 'name-text',
    kind: 'text',
    style: props.previewNameTextStyle,
    text: '姓名',
    textAlign: String(props.previewNameTextStyle.textAlign || 'left') as CanvasTextAlign,
    verticalAlign: 'center',
  },
])

const previewHotspotItems = computed<PreviewHotspotItem[]>(() => [
  {
    key: 'name-box',
    style: {
      ...props.previewNameBoxStyle,
      cursor: 'move',
      zIndex: 3,
    },
    ariaLabel: '姓名框拖拽热区',
    onPointerdown: (event: PointerEvent) => props.startPreviewDrag('name-box', event),
  },
  {
    key: 'name-text',
    style: {
      ...props.previewNameTextStyle,
      cursor: 'move',
      zIndex: 4,
    },
    ariaLabel: '拖动姓名文本',
    onPointerdown: (event: PointerEvent) => props.startPreviewDrag('name-text', event),
  },
  {
    key: 'name-handle',
    tag: 'button',
    style: {
      ...props.previewNameTextHandleStyle,
      width: '12px',
      height: '12px',
      padding: '0',
      border: '1px solid #8bc6ff',
      background: '#1f4f7f',
      cursor: 'move',
      zIndex: 5,
    },
    ariaLabel: '拖动姓名文本手柄',
    onPointerdown: (event: PointerEvent) => props.startPreviewDrag('name-text', event),
  },
  {
    key: 'talk-box',
    style: {
      ...props.previewTalkBoxStyle,
      cursor: 'move',
      zIndex: 1,
    },
    ariaLabel: '对话框拖拽热区',
    onPointerdown: (event: PointerEvent) => props.startPreviewDrag('talk-box', event),
  },
  {
    key: 'talk-text',
    style: {
      ...props.previewTalkTextRegionStyle,
      cursor: 'move',
      zIndex: 2,
    },
    ariaLabel: '对话文本拖拽热区',
    onPointerdown: (event: PointerEvent) => props.startPreviewDrag('talk-text', event),
    resizeHandle: {
      style: {
        position: 'absolute',
        right: '0',
        bottom: '0',
        width: '14px',
        height: '14px',
        borderLeft: '1px solid #8bc6ff',
        borderTop: '1px solid #8bc6ff',
        background: '#1f4f7f',
        cursor: 'nwse-resize',
      },
      ariaLabel: '调整对话文本区域大小',
      onPointerdown: (event: PointerEvent) => props.startPreviewResize(event),
    },
  },
])
</script>

<template>
  <div v-if="portraitLayout" class="settings-page settings-portrait-page dialog-page-portrait">
    <section class="settings-preview-card settings-portrait-preview dialog-preview-card">
      <CanvasPreviewSurface
        host-class="settings-preview-shell dialog-preview-shell"
        stage-class="settings-preview-stage dialog-preview-stage"
        :design-width="screenWidth"
        :design-height="screenHeight"
        :draw-items="previewDrawItems"
        :hotspot-items="previewHotspotItems"
        :guide-active="previewGuideActive"
        @stage-resize="onPreviewStageResize"
      />
    </section>

    <div class="settings-portrait-props">
      <section class="setting-card">
        <div class="card-title">对话框</div>

        <label class="field-block">
          <ResourceSelectField
            :model-value="talkSetting.TalkWin.BackPic.name"
            category="graphic"
            :base-folder-path="graphicBaseFolderPath"
            display-mode="full"
            store-mode="relative"
            picker-title="选择对话框图片"
            @update:model-value="updateTalkDialogBackPic"
          />
        </label>

        <label class="field-block">
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="talkSetting.TalkWin.X" class="cell-input sm" />
            <NumberStepperField v-model="talkSetting.TalkWin.Y" class="cell-input sm" />
          </div>
        </label>
      </section>

      <section class="setting-card">
        <div class="card-title">姓名框</div>

        <label class="field-block">
          <ResourceSelectField
            :model-value="talkSetting.NameWinS.BackPic.name"
            category="graphic"
            :base-folder-path="graphicBaseFolderPath"
            display-mode="full"
            store-mode="relative"
            picker-title="选择姓名框图片"
            @update:model-value="updateNameDialogBackPic"
          />
        </label>

        <label class="field-block">
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="talkSetting.NameWinS.x" class="cell-input sm" />
            <NumberStepperField v-model="talkSetting.NameWinS.y" class="cell-input sm" />
          </div>
        </label>
      </section>

      <section class="setting-card">
        <div class="card-title">对话文本</div>

        <label class="field-block">
          <span class="field-label">位置</span>
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="talkSetting.TalkWin.p_x1" class="cell-input sm" />
            <NumberStepperField v-model="talkSetting.TalkWin.p_y1" class="cell-input sm" />
          </div>
        </label>

        <label class="field-block">
          <span class="field-label">大小</span>
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="talkSetting.TalkWin.p_viewW" class="cell-input sm" :min="0" />
            <NumberStepperField v-model="talkSetting.TalkWin.p_viewH" class="cell-input sm" :min="0" />
          </div>
        </label>
      </section>

      <section class="setting-card">
        <div class="card-title">姓名文本</div>

        <label class="field-block">
          <span class="field-label">位置</span>
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="talkSetting.NameWinS.P_x" class="cell-input sm" />
            <NumberStepperField v-model="talkSetting.NameWinS.P_y" class="cell-input sm" />
          </div>
        </label>

        <label class="field-check">
          <input v-model="talkSetting.NameWinS.IsCenter" type="checkbox" />
          <span>姓名在姓名框中居中显示</span>
        </label>
      </section>

      <section class="setting-card">
        <div class="card-title">文本选项</div>

        <label class="field-block">
          <ImageOptionPickerField
            :model-value="talkSetting.SelectButton"
            :items="imageOptionPickerItems"
            :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
            title="选择文本选项框图片选项"
            @update:model-value="updateTextChoiceBackImage"
          />
        </label>
      </section>
    </div>
  </div>

  <div v-else class="settings-page dialog-page">
    <div class="dialog-grid">
      <div class="dialog-left-column">
        <section class="setting-card">
          <div class="card-title">对话框</div>

          <label class="field-block">
            <ResourceSelectField
              :model-value="talkSetting.TalkWin.BackPic.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择对话框图片"
              @update:model-value="updateTalkDialogBackPic"
            />
          </label>

          <label class="field-block">
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="talkSetting.TalkWin.X" class="cell-input sm" />
              <NumberStepperField v-model="talkSetting.TalkWin.Y" class="cell-input sm" />
            </div>
          </label>
        </section>

        <section class="setting-card">
          <div class="card-title">姓名框</div>

          <label class="field-block">
            <ResourceSelectField
              :model-value="talkSetting.NameWinS.BackPic.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择姓名框图片"
              @update:model-value="updateNameDialogBackPic"
            />
          </label>

          <label class="field-block">
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="talkSetting.NameWinS.x" class="cell-input sm" />
              <NumberStepperField v-model="talkSetting.NameWinS.y" class="cell-input sm" />
            </div>
          </label>
        </section>

        <section class="setting-card">
          <div class="card-title">对话文本</div>

          <label class="field-block">
            <span class="field-label">位置</span>
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="talkSetting.TalkWin.p_x1" class="cell-input sm" />
              <NumberStepperField v-model="talkSetting.TalkWin.p_y1" class="cell-input sm" />
            </div>
          </label>

          <label class="field-block">
            <span class="field-label">大小</span>
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="talkSetting.TalkWin.p_viewW" class="cell-input sm" :min="0" />
              <NumberStepperField v-model="talkSetting.TalkWin.p_viewH" class="cell-input sm" :min="0" />
            </div>
          </label>
        </section>

        <section class="setting-card">
          <div class="card-title">姓名文本</div>

          <label class="field-block">
            <span class="field-label">位置</span>
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="talkSetting.NameWinS.P_x" class="cell-input sm" />
              <NumberStepperField v-model="talkSetting.NameWinS.P_y" class="cell-input sm" />
            </div>
          </label>

          <label class="field-check">
            <input v-model="talkSetting.NameWinS.IsCenter" type="checkbox" />
            <span>姓名在姓名框中居中显示</span>
          </label>
        </section>

        <section class="setting-card">
          <div class="card-title">文本选项</div>

          <label class="field-block">
            <ImageOptionPickerField
              :model-value="talkSetting.SelectButton"
              :items="imageOptionPickerItems"
              :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
              title="选择文本选项框图片选项"
              @update:model-value="updateTextChoiceBackImage"
            />
          </label>
        </section>
      </div>

      <section class="settings-preview-card dialog-preview-card">
        <CanvasPreviewSurface
          host-class="settings-preview-shell dialog-preview-shell"
          stage-class="settings-preview-stage dialog-preview-stage"
          :design-width="screenWidth"
          :design-height="screenHeight"
          :draw-items="previewDrawItems"
          :hotspot-items="previewHotspotItems"
          :guide-active="previewGuideActive"
          @stage-resize="onPreviewStageResize"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.dialog-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
}

.dialog-left-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.dialog-preview-card {
  min-height: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.dialog-preview-shell {
  width: 100%;
  height: 100%;
  padding: 10px;
}

.dialog-preview-box {
  position: absolute;
  border: 1px dashed var(--theme-accent);
  background: #2b3038;
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
  cursor: move;
  user-select: none;
}

.dialog-preview-talk-box {
  z-index: 1;
}

.dialog-preview-name-box {
  z-index: 3;
}

.dialog-preview-box--image {
  border-style: solid;
  background: #242931;
}

.dialog-preview-box img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}

.dialog-preview-name-text {
  position: absolute;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 4;
  user-select: none;
  pointer-events: none;
}

.dialog-preview-name-text-handle {
  position: absolute;
  z-index: 5;
  width: 12px;
  height: 12px;
  padding: 0;
  border: 1px solid #8bc6ff;
  background: #1f4f7f;
  cursor: move;
}

.dialog-preview-text-region {
  position: absolute;
  border: 1px solid #6fb6ff;
  background: #365882;
  box-sizing: border-box;
  padding: 6px;
  overflow: hidden;
  z-index: 2;
  cursor: move;
  user-select: none;
}

.dialog-preview-text-body {
  line-height: 1.4;
  white-space: pre-wrap;
}

.dialog-preview-text-resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  border-left: 1px solid #8bc6ff;
  border-top: 1px solid #8bc6ff;
  background: #1f4f7f;
  cursor: nwse-resize;
}

@media (max-width: 1180px) and (orientation: landscape) {
  .dialog-grid {
    grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  }
}

@media (max-width: 980px) and (orientation: portrait) {
  .dialog-grid {
    grid-template-columns: 1fr;
  }
}
</style>
